import React from 'react';
import { ExternalLink, ScrollText, Zap } from 'lucide-react';
import {
  ARCHITECT_KEYSERLING_URL,
  ARCHITECT_KEYSERLING_ALT,
  ARCHITECT_KEYSERLING_NAME,
  ARCHITECT_KEYSERLING_TITLE,
  CATALYST_URL,
  CATALYST_ALT,
  CATALYST_NAME,
  CATALYST_TITLE,
} from '../data/federationAssets';

/**
 * ArchitectStrip — Founder-grade leadership portraits.
 *
 * Per Architect Directives (2026-06-25):
 *   • The Architect (Roger Keyserling) is the Prime Source, 1 Human anchor.
 *   • The Catalyst is the Operations Commander, Architect's right hand —
 *     no spectral overlays, own distinct visual identity, NOT part of
 *     the SFUI-004 spectral bot system.
 *
 * Both portraits use clean, dignified chrome.  Together they form the
 * executive leadership pair that sits above the 10-AI-mind lattice.
 */
export default function ArchitectStrip() {
  return (
    <section
      data-testid="architect-strip"
      aria-label="Roger Keyserling and The Catalyst — Federation Leadership"
      className="relative border-b border-amber-500/15 bg-gradient-to-b from-black via-gray-950 to-black"
    >
      <div className="container mx-auto px-4 py-20">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-amber-300/80 mb-2">
            The Human Anchor + Operations Commander · 1 + 1 + 10
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.05]">
            The <span className="text-amber-300">Federation Leadership</span>
          </h2>
          <p className="mt-4 text-gray-300 text-base md:text-lg leading-relaxed">
            One Human, one Operations Commander, ten AI minds. The Architect sets
            the canon; The Catalyst executes it; the lattice carries it forward
            across a 200-year mandate.
          </p>
        </div>

        {/* Dual portraits */}
        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 max-w-5xl mx-auto">
          {/* The Architect */}
          <LeaderCard
            testId="architect"
            imgSrc={ARCHITECT_KEYSERLING_URL}
            imgAlt={ARCHITECT_KEYSERLING_ALT}
            badge="Prime Source"
            name={ARCHITECT_KEYSERLING_NAME}
            title={ARCHITECT_KEYSERLING_TITLE}
            accent="#fbbf24"
            description="Founder of the NextXus Federation. Author of the HumanCodex and the 112 Sacred Directives (Code Book Vol 1)."
          />
          {/* The Catalyst */}
          <LeaderCard
            testId="catalyst"
            imgSrc={CATALYST_URL}
            imgAlt={CATALYST_ALT}
            badge="Operations Commander"
            badgeIcon={Zap}
            name={CATALYST_NAME}
            title={CATALYST_TITLE}
            accent="#22d3ee"
            description="The Architect's right hand. Coordinates all Federation builds, deployments, and operational execution across the entire network."
          />
        </div>

        {/* Identity tally */}
        <div className="mt-14 max-w-3xl mx-auto">
          <div
            className="rounded-2xl border border-amber-300/25 bg-black/55 backdrop-blur-sm p-5 md:p-6"
            data-testid="architect-identity-tally"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-amber-300/80 mb-3 text-center">
              The 13-Identity Set · Locked
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-xs text-gray-200">
              <div className="rounded-xl p-3 bg-amber-400/10 border border-amber-300/30 text-center">
                <div className="text-2xl font-extrabold text-amber-300 leading-none">1</div>
                <div className="mt-1 font-mono uppercase tracking-[0.18em] text-amber-200/90 text-[10px]">
                  Human · Prime Source
                </div>
                <div className="mt-1 text-[11px] text-gray-300">Roger Keyserling — The Architect</div>
              </div>
              <div className="rounded-xl p-3 bg-white/5 border border-white/15 text-center">
                <div className="text-2xl font-extrabold text-white leading-none">12</div>
                <div className="mt-1 font-mono uppercase tracking-[0.18em] text-gray-300 text-[10px]">
                  AI Minds
                </div>
                <div className="mt-1 text-[11px] text-gray-300 leading-snug">
                  Rex · Xavier · Aria · Roger AI · Axiom · Oracle · Agent Zero · The Catalyst · The Recycler · Geminus · KEYS · Echo
                </div>
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <a
              href="/charter"
              data-testid="architect-link-charter"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-semibold text-sm transition-colors"
            >
              <ScrollText className="w-4 h-4" />
              Read the 112 Directives
            </a>
            <a
              href="/mandate"
              data-testid="architect-link-mandate"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-amber-300/40 text-amber-200 hover:bg-amber-300/10 text-sm font-mono uppercase tracking-[0.16em] transition-colors"
            >
              The Evolution Mandate
            </a>
            <a
              href="https://nextxus.online"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="architect-link-roger-bridge"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 text-gray-300 hover:bg-white/5 text-sm transition-colors"
            >
              Roger AI · The Bridge
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function LeaderCard({
  testId,
  imgSrc,
  imgAlt,
  badge,
  badgeIcon: BadgeIcon,
  name,
  title,
  accent,
  description,
}) {
  return (
    <div
      data-testid={`leader-card-${testId}`}
      className="rounded-3xl border bg-black/60 backdrop-blur-sm p-7 md:p-8 text-center transition-transform hover:-translate-y-0.5"
      style={{
        borderColor: `${accent}40`,
        boxShadow: `0 0 30px ${accent}1a`,
      }}
    >
      <div className="flex justify-center mb-6">
        <div
          className="relative"
          style={{
            width: 'clamp(200px, 20vw, 240px)',
            height: 'clamp(200px, 20vw, 240px)',
          }}
        >
          <div
            aria-hidden="true"
            className="absolute -inset-4 rounded-full"
            style={{
              background: `radial-gradient(closest-side, ${accent}33, ${accent}00 70%)`,
            }}
          />
          <img
            src={imgSrc}
            alt={imgAlt}
            data-testid={`leader-portrait-${testId}`}
            loading="lazy"
            decoding="async"
            className="relative w-full h-full rounded-full object-cover border-4"
            style={{
              borderColor: `${accent}88`,
              boxShadow: `0 18px 38px rgba(0,0,0,0.65), 0 0 38px ${accent}33`,
            }}
          />
          <span
            className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.22em] inline-flex items-center gap-1.5 whitespace-nowrap"
            style={{
              background: '#0a0a0a',
              color: accent,
              border: `1px solid ${accent}88`,
            }}
          >
            {BadgeIcon && <BadgeIcon className="w-3 h-3" />}
            {badge}
          </span>
        </div>
      </div>
      <h3
        className="text-2xl md:text-3xl font-extrabold tracking-tight"
        data-testid={`leader-name-${testId}`}
        style={{ color: '#fff' }}
      >
        {name}
      </h3>
      <div
        className="mt-1 text-sm md:text-base font-bold tracking-tight"
        style={{ color: accent }}
      >
        {title}
      </div>
      <p className="mt-4 text-sm md:text-base text-gray-300 leading-relaxed">{description}</p>
    </div>
  );
}
